import React from "react";
import { Tag, Typography, Table, Button, Empty, message } from "antd";
import { DollarOutlined, PrinterOutlined } from "@ant-design/icons";
import axios from "axios";
import THEME from "../theme";
import { formatMoney, formatDate } from "../utils";
import StyledCard from "../components/StyledCard";
import { BASE_URL_BEN } from "../../../../API/apiUrls";
import { PaymentLawyerPdfJs } from "../../export/PaymentLawyerPdfJs";
import { PaymentArLawyerPdfjs } from "../../export/PaymentArLawyerPdfjs";

const { Text } = Typography;

// ยอดชำระต้น: ถ้า PAYAMT_N = "0" ให้ใช้ PAYAMT แทน (ไม่ใช้ PAYAMT_V)
const getPayAmtN = (record) =>
  Number(record?.PAYAMT_N || 0) === 0
    ? record?.PAYAMT - record?.PAYAMT_V
    : record?.PAYAMT_N;

// PAYAMT_V ถ้ามีค่า ให้หักออกจาก PAYAMT (เหลือเฉพาะส่วนที่ไม่ใช่ดอกเบี้ย)
const adjustPayAmt = (record) => {
  const payAmt = Number(record?.PAYAMT || 0);
  const payAmtV = Number(record?.PAYAMT_V || 0);
  const diff = payAmt - payAmtV;
  return payAmtV && diff !== 0 ? diff : payAmt;
};

const PaymentTab = ({ judgeData, token }) => {
  const buildPaymentColumns = () => [
    {
      title: "ลำดับ",
      key: "index",
      width: 60,
      align: "center",
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "เลขที่บิล",
      dataIndex: "TMBILL",
      key: "TMBILL",
      render: (val) => <Text style={{ fontFamily: "monospace" }}>{val}</Text>,
    },
    {
      title: "วันที่บิล",
      dataIndex: "TMBILDT",
      key: "TMBILDT",
      render: (val) => formatDate(val),
    },
    {
      title: "ชำระโดย",
      dataIndex: "PAYTYP",
      key: "PAYTYP",
    },
    {
      title: "รหัสชำระ",
      dataIndex: "PAYFOR",
      key: "PAYFOR",
    },
    // {
    //   title: "รายการ",
    //   dataIndex: "FORDESC",
    //   key: "FORDESC",
    // },
    // {
    //   title: "รับเข้า",
    //   dataIndex: "NETPAY",
    //   key: "NETPAY",
    //   align: "right",
    //   render: (val) => (
    //     <Text strong style={{ color: THEME.success }}>
    //       {formatMoney(val)} บาท
    //     </Text>
    //   ),
    // },
    {
      title: "ตัดลูกหนี้",
      key: "PAYAMT_N",
      align: "right",
      render: (_, record) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(getPayAmtN(record))} บาท
        </Text>
      ),
    },
    {
      title: "ตัดต้น",
      key: "PAYAMT_N",
      align: "right",
      render: (_, record) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(record.__principalCut)} บาท
        </Text>
      ),
    },
    {
      title: "เบี้ยปรับ",
      key: "PAYAMT_V",
      align: "right",
      render: (_, record) => (
        <Text strong style={{ color: THEME.warning }}>
          {formatMoney(record.PAYAMT_V)} บาท
        </Text>
      ),
    },
    {
      title: "ดอกเบี้ย",
      dataIndex: "DISCT",
      key: "DISCT",
      align: "right",
      render: (_, record) => (
        <Text strong style={{ color: THEME.danger }}>
          {formatMoney(record.__interestCut)} บาท
        </Text>
      ),
    },
    {
      title: "- ส่วนลด",
      dataIndex: "DISCT",
      key: "DISCT",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.danger }}>
          {val === "0" ? null : "-"}
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: `ตัดต้นทุนเก่า`,
      key: "__oldCapitalCut",
      align: "right",
      render: (_, record) =>
        record.__oldCapitalCut === null ? (
          "-"
        ) : (
          <Text strong style={{ color: THEME.danger }}>
            {formatMoney(record.__oldCapitalCut)} บาท
          </Text>
        ),
    },
    {
      title: "ทุนเก่าคงเหลือ",
      key: "__oldCapitalBalance",
      align: "right",
      render: (_, record) =>
        record.__oldCapitalBalance === null ? (
          "-"
        ) : (
          <Text strong style={{ color: THEME.danger }}>
            {formatMoney(record.__oldCapitalBalance)} บาท
          </Text>
        ),
    },
    {
      title: "วันที่ชำระ",
      dataIndex: "PAYDT",
      key: "PAYDT",
      render: (val) => formatDate(val),
    },
    {
      title: "รับสุทธิ",
      dataIndex: "NETPAY",
      key: "NETPAY",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "FLAG",
      key: "FLAG",
      width: 40,
      align: "right",
      render: (val) => (
        <Tag color={val === "C" ? "red" : "green"}>
          {val === "C" ? "ยกเลิก" : "รับปกติ"}
        </Tag>
      ),
    },
    {
      title: "ผู้รับ",
      dataIndex: "INPUT_BY",
      key: "INPUT_BY",
      align: "center",
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 140,
      render: (_, record) => (
        <Button
          icon={<PrinterOutlined />}
          // disabled={record.FLAG === "C"} // บิลยกเลิกไม่ให้พิมพ์
          onClick={() => handlePrintReceipt(record, record.__printType)}
        >
          พิมพ์ใบเสร็จ
        </Button>
      ),
    },
  ];

  // สร้าง summary รวมทุกคอลัมน์เงิน (ไม่รวมรายการที่ยกเลิก)
  const buildPaymentSummary = (data) => () => {
    const rows = (data || []).filter((r) => r.FLAG !== "C");
    const sumBy = (field) =>
      rows.reduce((sum, r) => sum + Number(r[field] || 0), 0);
    const sumPayAmtN = () =>
      rows.reduce((sum, r) => sum + Number(getPayAmtN(r) || 0), 0);
    // "ตัดต้น" และ "ดอกเบี้ย": รวมจากค่าที่คำนวณแบบไล่ตัด (waterfall) ต่อ record ไว้แล้ว ตรงกับคอลัมน์
    const sumPayAmtNTon = () =>
      rows.reduce((sum, r) => sum + Number(r.__principalCut || 0), 0);
    const discInterestTotal = rows.reduce(
      (sum, r) => sum + Number(r.__interestCut || 0),
      0,
    );
    // "ตัดต้นทุนเก่า": รวมยอดที่ตัดทุนเก่า, "ทุนเก่าคงเหลือ": creditDebtor - ยอดที่ตัดไปแล้ว
    const oldCapitalCutTotal = rows.reduce(
      (sum, r) => sum + Number(r.__oldCapitalCut || 0),
      0,
    );
    const oldCapitalBalanceTotal = Math.max(
      Number(judgeData?.judge?.[0]?.creditDebtor || 0) - oldCapitalCutTotal,
      0,
    );

    const MoneyCell = ({ value, index, color = THEME.success }) => (
      <Table.Summary.Cell index={index} align="right">
        <Text strong style={{ color }}>
          {formatMoney(value)} บาท
        </Text>
      </Table.Summary.Cell>
    );

    return (
      <Table.Summary.Row style={{ background: THEME.light }}>
        {/* ลำดับ, เลขที่บิล, วันที่บิล, ชำระโดย, รหัสชำระ */}
        <Table.Summary.Cell index={0} colSpan={5}>
          <Text strong>รวมทั้งหมด (ไม่รวมยกเลิก) </Text>
        </Table.Summary.Cell>
        {/* <MoneyCell index={5} value={sumBy("PAYAMT")} /> รับเข้า */}
        <MoneyCell index={5} value={sumPayAmtN()} /> {/* ตัดลูกหนี้ */}
        <MoneyCell index={6} value={sumPayAmtNTon()} /> {/* ตัดต้น */}
        <MoneyCell
          index={7}
          value={sumBy("PAYAMT_V")}
          color={THEME.warning}
        />{" "}
        {/* เบี้ยปรับ */}
        <MoneyCell
          index={8}
          value={discInterestTotal}
          color={THEME.danger}
        />{" "}
        {/* ดอกเบี้ย */}
        <MoneyCell
          index={9}
          value={sumBy("DISCT") > 0 ? -sumBy("DISCT") : sumBy("DISCT")}
          color={THEME.danger}
        />{" "}
        {/* - ส่วนลด */}
        <MoneyCell
          index={10}
          value={oldCapitalCutTotal}
          color={THEME.danger}
        />{" "}
        {/* ตัดต้นทุนเก่า */}
        <MoneyCell
          index={11}
          value={oldCapitalBalanceTotal}
          color={THEME.danger}
        />{" "}
        {/* ทุนเก่าคงเหลือ */}
        <Table.Summary.Cell index={12} /> {/* วันที่ชำระ */}
        <MoneyCell index={13} value={sumBy("NETPAY")} /> {/* รับสุทธิ */}
        {/* สถานะ, ผู้รับ, การจัดการ */}
        <Table.Summary.Cell index={14} colSpan={3} />
      </Table.Summary.Row>
    );
  };

  // ค่า pagination ใช้ร่วมกัน
  const paymentPagination = {
    defaultPageSize: 5,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  };

  // ---------- ฟังก์ชันพิมพ์ใบเสร็จ (โหลดข้อมูลจาก API แล้วสร้าง PDF ด้วย jsPDF) ----------
  const handlePrintReceipt = async (record, type) => {
    // type: "normal" = chqtran → PaymentLawyerPdfJs
    // type: "ar"     = chqtran_arothr → PaymentArLawyerPdfjs
    if (!record?.TMBILL) return message.warning("ไม่พบใบเสร็จ");

    const isAr = type === "ar";
    const msgKey = isAr ? "load_process_ar_print" : "load_process_print";

    // หน้าเดิมใช้ record.REFDOCNO แต่ข้อมูลใน judgeData ใช้ CONTNO
    const contno = record?.CONTNO || record?.REFDOCNO;

    const url = isAr
      ? `${BASE_URL_BEN}/auth/print-payment-lawyer-ar`
      : `${BASE_URL_BEN}/auth/print-payment-lawyer`;

    const finalPayload = isAr
      ? { TMBILL: record?.TMBILL, CONTNO: contno }
      : { TMBILL: record?.TMBILL, CONTNO: contno, PAYFOR: record?.PAYFOR };

    try {
      message.loading({
        content: "กำลังโหลดข้อมูลใบเสร็จ...",
        key: msgKey,
      });

      const response = await axios.post(url, finalPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data) {
        message.success({
          content: "โหลดข้อมูลสำเร็จ",
          key: msgKey,
        });

        if (isAr) await PaymentArLawyerPdfjs(response.data);
        else await PaymentLawyerPdfJs(response.data);
      } else {
        message.warning({
          content: "ไม่พบข้อมูลใบเสร็จ",
          key: msgKey,
        });
      }
    } catch (error) {
      console.log("error print", error);
      message.error({
        content: "เกิดข้อผิดพลาดในการโหลดใบเสร็จ",
        key: msgKey,
      });
    }
  };

  if (!judgeData?.chqtran?.length && !judgeData?.chqtran_arothr?.length)
    return <Empty description="ไม่มีข้อมูล" />;

  // ปรับ PAYAMT (หัก PAYAMT_V) และตัด record ที่ PAYAMT เหลือ 0 ทิ้ง
  const normalRows = (judgeData?.chqtran || [])
    .map((r) => ({ ...r, PAYAMT: adjustPayAmt(r), __printType: "normal" }))
    .filter((r) => Number(r.PAYAMT) !== 0);

  const arRowsAdjusted = (judgeData?.chqtran_arothr || [])
    .map((r) => ({ ...r, PAYAMT: adjustPayAmt(r) }))
    .filter((r) => Number(r.PAYAMT) !== 0);

  // รวม chqtran_arothr ทั้งหมดเป็น record เดียว ใช้ชื่อ "ลูกหนี้อื่น ๆ"
  const sumField = (rows, field) =>
    rows.reduce((sum, r) => sum + Number(r[field] || 0), 0);

  const arRow = arRowsAdjusted.length
    ? [
        {
          ...arRowsAdjusted[0],
          wctId: "ar-merged",
          PAYFOR: "",
          FORDESC: "ลูกหนี้อื่น ๆ",
          PAYAMT: sumField(arRowsAdjusted, "PAYAMT"),
          PAYAMT_N: sumField(arRowsAdjusted, "PAYAMT_N"),
          DISCT: sumField(arRowsAdjusted, "DISCT"),
          NETPAY: sumField(arRowsAdjusted, "NETPAY"),
          __printType: "ar",
        },
      ]
    : [];

  // รวมข้อมูล chqtran (ชำระปกติ) และ chqtran_arothr (ลูกหนี้อื่น ๆ) เป็นตารางเดียว
  const combinedData = [...normalRows, ...arRow];

  // เมื่อต้นคงเหลือ (TONBALANCE) = 0 (ปิดยอดต้นครบแล้ว) ให้ไล่ตัด PAYAMT_N ของแต่ละ record
  // เข้าวงเงิน "ตัดต้น" (creditDebtor) ก่อนจนหมด แล้วเศษที่เหลือค่อยไปตัดวงเงิน "ดอกเบี้ย"
  // (totalJugde - creditDebtor) ต่อไปเรื่อย ๆ จนหมด (record ถัดไปเริ่มตัดต้นที่ 0)
  const useWaterfall = Number(judgeData?.judge?.[0]?.TONBALANCE || 0) === 0;
  let principalPool = Number(judgeData?.judge?.[0]?.creditDebtor || 0);
  let interestPool =
    Number(judgeData?.judge?.[0]?.totalJugde || 0) - principalPool;

  combinedData.forEach((record) => {
    const payAmtN = getPayAmtN(record);
    if (!useWaterfall) {
      record.__principalCut = payAmtN;
      record.__interestCut = 0;
      return;
    }
    // บิลยกเลิกไม่นำมาไล่ตัดวงเงิน (ให้ตรงกับ summary ที่ไม่รวมรายการยกเลิก)
    if (record.FLAG === "C") {
      record.__principalCut = 0;
      record.__interestCut = 0;
      return;
    }
    const principalCut = Math.min(payAmtN, Math.max(principalPool, 0));
    principalPool -= principalCut;
    const remainder = payAmtN - principalCut;
    const interestCut = Math.min(remainder, Math.max(interestPool, 0));
    interestPool -= interestCut;
    record.__principalCut = principalCut;
    record.__interestCut = interestCut;
  });

  // ตัดต้นทุนเก่า / ทุนเก่าคงเหลือ: นำยอดตัดต้น (__principalCut) ไปตัด creditDebtor
  // ไล่ไปทีละ record จนทุนเก่าเหลือ 0 record ที่มาหลังจากทุนเก่าหมดแล้วให้แสดงเป็น "-"
  let oldCapitalBalance = Number(judgeData?.judge?.[0]?.creditDebtor || 0);
  combinedData.forEach((record) => {
    if (record.FLAG === "C" || oldCapitalBalance <= 0) {
      record.__oldCapitalCut = null;
      record.__oldCapitalBalance = null;
      return;
    }
    const oldCapitalCut = Math.min(
      Number(record.__principalCut || 0),
      oldCapitalBalance,
    );
    oldCapitalBalance -= oldCapitalCut;
    record.__oldCapitalCut = oldCapitalCut;
    record.__oldCapitalBalance = oldCapitalBalance;
  });

  return (
    <StyledCard
      title={
        judgeData?.loanNew
          ? `ตารางการรับชำระรวม(ทำยอม) ทุนเก่า ${judgeData?.judge?.[0].creditPercen}% ${formatMoney(judgeData?.judge?.[0].creditDebtor)}`
          : `ตารางการรับชำระรวม ทุนเก่า ${judgeData?.judge?.[0].creditPercen}% ${formatMoney(judgeData?.judge?.[0].creditDebtor)}`
      }
      icon={
        <DollarOutlined
          style={{
            color: judgeData?.loanNew ? THEME.primary : THEME.danger,
            fontSize: 18,
          }}
        />
      }
      style={{ marginBottom: 16 }}
    >
      <Table
        dataSource={combinedData}
        columns={buildPaymentColumns()}
        rowKey="wctId"
        pagination={paymentPagination}
        size="middle"
        scroll={{ x: 1500 }}
        summary={buildPaymentSummary(combinedData)}
      />
    </StyledCard>
  );
};

export default PaymentTab;
